import React, { useState } from 'react';
import './App.css';
import './PS.css';

const categories = [
  {
    id: 'ai-ml',
    label: 'AI / ML',
    color: '#ff8500',
    problems: [
      {
        id: 'PS-01',
        title: 'Fake News / Deepfake Detector',
        problem:
          'The internet has made information instantly accessible, but it has also made it easier to spread misleading and manipulated content at scale. With advancements in generative AI, creating highly realistic fake news articles, images, videos, and audio has become trivial even for non-experts. Such content can influence public opinion, damage reputations, and create confusion during critical situations such as elections, financial decisions, or emergencies. The speed at which misinformation spreads far exceeds the speed of manual verification, making traditional fact-checking methods insufficient. There is a growing need for automated systems that can assist in identifying manipulated content in real time.',
        objective:
          'Build a system that can analyze different forms of content and determine whether it is authentic or manipulated. The system should aim to provide reliable and consistent detection while also offering a degree of confidence or reasoning behind its decisions. The goal is to assist users, platforms, or organizations in quickly filtering out misleading content and improving trust in digital information ecosystems.'
      },
      {
        id: 'PS-02',
        title: 'AI Creditworthiness Analyzer',
        problem:
          'A significant portion of the population remains excluded from formal credit systems due to lack of traditional financial history. Individuals such as freelancers, students, or those in informal sectors often have stable income and responsible financial behavior but are still considered high risk by conventional models. This results in reduced financial inclusion and missed opportunities for both borrowers and lenders. Existing systems rely heavily on limited metrics and fail to capture real-world financial patterns and behavior.',
        objective:
          'Design a system that evaluates creditworthiness by analyzing a broader set of financial and behavioral indicators. The system should aim to provide a more realistic and inclusive assessment of an individual\'s ability to repay, enabling better decision-making for lenders while expanding access to credit for underserved populations.'
      },
      {
        id: 'PS-03',
        title: 'Fraud Detection in Digital Transactions',
        problem:
          'As digital payments become the dominant mode of financial transactions, fraud has evolved in complexity and scale. Attackers continuously exploit system vulnerabilities through techniques such as account takeovers, transaction manipulation, and identity misuse. These activities often occur in real time, making detection challenging with static rule-based systems. At the same time, overly aggressive detection can lead to false positives, disrupting legitimate user activity and reducing trust in the system.',
        objective:
          'Develop a system that can analyze transaction behavior and detect anomalies that may indicate fraud. The system should focus on identifying patterns that deviate from normal behavior while maintaining a balance between detection accuracy and user experience. The goal is to enable faster and more reliable fraud prevention without unnecessarily blocking genuine transactions.'
      },
      {
        id: 'PS-04',
        title: 'Loan Default Prediction',
        problem:
          'Predicting whether a borrower will default on a loan is a complex decision influenced by multiple factors, including financial stability, spending behavior, and economic conditions. Traditional models often rely on limited parameters and may fail to capture the dynamic nature of borrower behavior. Incorrect predictions can lead to financial losses for institutions or unfair denial of credit to deserving individuals.',
        objective:
          'Build a system that estimates the likelihood of loan default using available borrower and loan data. The system should aim to provide meaningful risk insights that support more informed lending decisions, while also highlighting key factors influencing the prediction to improve transparency.'
      },
      {
        id: 'PS-05',
        title: 'AI-Based Exam Cheating Detection',
        problem:
          'The transition to online examinations has introduced significant challenges in maintaining academic integrity. Without physical supervision, institutions must rely on digital signals to detect dishonest behavior. However, existing solutions are often intrusive, unreliable, or easily bypassed. There is a need for systems that can monitor behavior effectively without compromising user experience or privacy.',
        objective:
          'Create a system that can analyze user behavior during online exams and identify patterns that may indicate cheating. The system should aim to detect anomalies such as unusual activity, inconsistent behavior, or suspicious patterns while minimizing false accusations and maintaining fairness.'
      },
      {
        id: 'PS-06',
        title: 'Customer Emotion Analytics (Voice)',
        problem:
          'In real-world interactions, emotional context plays a critical role in communication. Businesses often rely on textual data or basic metrics, ignoring the emotional cues present in voice such as tone, pitch, and stress. This results in incomplete understanding of customer sentiment and missed opportunities for improving service quality and responsiveness.',
        objective:
          'Build a system that analyzes voice input to detect emotional states and patterns. The system should aim to convert raw audio signals into meaningful insights that can help organizations better understand user experiences and respond more effectively.'
      }
    ]
  },
  {
    id: 'duality',
    label: 'Duality AI Track',
    color: '#a855f7',
    problems: [
      {
        id: 'PS-07',
        title: 'Offroad Scene Segmentation',
        sponsor: 'Duality AI',
        problem:
          'Autonomous systems require accurate perception of their surroundings to make safe and effective decisions. In off-road environments, this becomes significantly more challenging due to irregular terrain, dynamic obstacles, and lack of structured data. Collecting and labeling real-world data for such environments is costly and time-intensive. Synthetic data provides an alternative, but models must still generalize well to unseen scenarios and varying conditions.',
        objective:
          'Develop a system that can interpret visual scenes and segment different elements of the environment using the provided dataset. The system should aim to achieve accurate and consistent segmentation while demonstrating the ability to generalize to new, unseen environments, supporting real-world autonomous applications.'
      }
    ]
  },
  {
    id: 'blockchain',
    label: 'Blockchain',
    color: '#3b82f6',
    problems: [
      {
        id: 'PS-08',
        title: 'Blockchain-based P2P Lending',
        problem:
          'Traditional lending systems rely on centralized institutions, which can introduce inefficiencies, delays, and lack of transparency. While peer-to-peer lending removes intermediaries, it still requires mechanisms to ensure trust, enforce agreements, and maintain accountability between participants.',
        objective:
          'Design a decentralized lending system that enables individuals to lend and borrow directly while ensuring secure transactions and transparent record-keeping. The system should aim to reduce dependency on intermediaries while maintaining trust through blockchain-based mechanisms.'
      },
      {
        id: 'PS-09',
        title: 'Blockchain-based Land Registration',
        problem:
          'Land ownership systems are often fragmented, paper-based, and vulnerable to fraud and manipulation. Verifying ownership can be time-consuming and prone to disputes due to lack of reliable and tamper-proof records.',
        objective:
          'Build a system that securely records and verifies land ownership using a decentralized approach. The system should aim to ensure transparency, prevent unauthorized modifications, and simplify the verification process for stakeholders.'
      },
      {
        id: 'PS-10',
        title: 'Blockchain-based Identity Verification',
        problem:
          'Digital identity systems are critical for access control and verification, yet they are often centralized and vulnerable to breaches, duplication, and misuse. Users have limited control over how their identity data is stored and shared, leading to privacy concerns.',
        objective:
          'Create a decentralized identity system that allows secure creation, verification, and controlled sharing of identity data. The system should aim to improve security while giving users greater ownership and control over their information.'
      }
    ]
  },
  {
    id: 'sustainability',
    label: 'Sustainability',
    color: '#22c55e',
    problems: [
      {
        id: 'PS-11',
        title: 'Energy Consumption Optimizer',
        problem:
          'Energy consumption is often inefficient due to lack of visibility into usage patterns and absence of actionable insights. Individuals and organizations frequently consume more energy than necessary, leading to increased costs and environmental impact. Even small inefficiencies, when scaled, can result in significant resource wastage.',
        objective:
          'Develop a system that analyzes energy usage patterns and provides intelligent recommendations to optimize consumption. The system should aim to help users make better decisions, reduce waste, and improve overall efficiency.'
      },
      {
        id: 'PS-12',
        title: 'Smart Waste Management System',
        problem:
          'Waste management systems commonly operate on fixed schedules rather than actual demand, leading to inefficiencies such as unnecessary collection trips or overflowing bins. This results in increased operational costs and environmental issues.',
        objective:
          'Build a system that improves how waste is monitored and managed by enabling more responsive and data-driven collection strategies. The goal is to optimize resource usage and improve overall efficiency of waste management systems.'
      }
    ]
  },
  {
    id: 'cybersecurity',
    label: 'Cyber Security',
    color: '#ef4444',
    problems: [
      {
        id: 'PS-13',
        title: 'Phishing Detection System',
        problem:
          'Phishing attacks are becoming increasingly sophisticated, often mimicking legitimate communication to deceive users. As these attacks evolve, it becomes harder for users to distinguish between genuine and malicious content, leading to data breaches and financial loss.',
        objective:
          'Design a system that can detect phishing attempts by analyzing communication patterns and identifying suspicious characteristics. The system should aim to assist users in avoiding harmful interactions and improve overall digital security.'
      },
      {
        id: 'PS-14',
        title: 'Network Intrusion Detection System',
        problem:
          'Modern networks generate large volumes of activity, making it difficult to manually identify unauthorized access or malicious behavior. Attackers often exploit subtle vulnerabilities that remain undetected until significant damage occurs.',
        objective:
          'Develop a system that continuously monitors network activity and identifies abnormal patterns that may indicate an intrusion. The goal is to enhance early detection and improve system security.'
      },
      {
        id: 'PS-15',
        title: 'Malware Classification System',
        problem:
          'Malware is continuously evolving, making traditional detection techniques less effective. New variants are designed to bypass signature-based systems, posing serious risks to systems and sensitive data.',
        objective:
          'Build a system that analyzes software characteristics or behavior to classify it as malicious or safe. The system should aim to provide adaptive and robust detection capable of handling new and unseen threats.'
      }
    ]
  }
];

function PSCard({ ps, accentColor }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <article className="ps-card edge-card" style={{ '--ps-accent': accentColor }}>
      <div className="ps-card-header" onClick={() => setExpanded((p) => !p)}>
        <div className="ps-id-badge" style={{ borderColor: accentColor, color: accentColor }}>
          {ps.id}
        </div>
        <div className="ps-card-title-wrap">
          <h3 className="ps-card-title">{ps.title}</h3>
          {ps.sponsor && (
            <span className="ps-sponsor-badge" style={{ color: accentColor, borderColor: accentColor }}>
              {ps.sponsor}
            </span>
          )}
        </div>
        <button
          className="ps-expand-btn"
          aria-label={expanded ? 'Collapse' : 'Expand'}
          style={{ color: accentColor }}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>

      {expanded && (
        <div className="ps-card-body">
          <div className="ps-block">
            <span className="ps-block-label" style={{ color: accentColor }}># Problem</span>
            <p>{ps.problem}</p>
          </div>
          <div className="ps-block">
            <span className="ps-block-label" style={{ color: accentColor }}># Objective</span>
            <p>{ps.objective}</p>
          </div>
        </div>
      )}
    </article>
  );
}

export default function ProblemStatements() {
  const [activeCategory, setActiveCategory] = useState(null);

  const filtered = activeCategory
    ? categories.filter((c) => c.id === activeCategory)
    : categories;

  const totalPS = categories.reduce((acc, c) => acc + c.problems.length, 0);

  return (
    <div className="ps-page">
      <div className="ps-hero">
        <div className="container">
          <p className="hero-kicker">HACKSTORM 2.26</p>
          <h1 className="ps-hero-title">
            Problem <span>Statements</span>
          </h1>
          <p className="ps-hero-sub">
            {totalPS} problem statements across {categories.length} tracks. Pick one, build something real.
          </p>

          <div className="ps-filter-bar">
            <button
              className={`ps-filter-btn ${!activeCategory ? 'active' : ''}`}
              onClick={() => setActiveCategory(null)}
            >
              All Tracks
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`ps-filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
                style={activeCategory === cat.id ? { borderColor: cat.color, color: cat.color } : {}}
                onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="ps-body container">
        {filtered.map((cat) => (
          <section key={cat.id} className="ps-category">
            <div className="ps-category-head">
              <h2 className="ps-category-title" style={{ color: cat.color }}>
                # {cat.label}
              </h2>
              <span className="ps-category-count">{cat.problems.length} PS</span>
            </div>

            <div className="ps-list">
              {cat.problems.map((ps) => (
                <PSCard key={ps.id} ps={ps} accentColor={cat.color} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="ps-cta-bar container">
        <p>Ready to build? Register on Devfolio and pick your problem statement.</p>
        <a
          href="https://hack-storm.devfolio.co"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-devfolio"
        >
          Apply on Devfolio
        </a>
      </div>
    </div>
  );
}
